import { useRequiredContext } from "@redotech/react-util/context";
import {
  getCarrierTrackingUrl,
  resolveCarrier,
} from "@redotech/redo-model/order-tracking/carriers/carrier-resolver";
import { OrderTrackingShipmentStatus } from "@redotech/redo-model/order-tracking/order-tracking-types";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { memo, useMemo } from "react";
import { Flex } from "../../../flex";
import { ExternalLink } from "../../../link";
import { PickupConfig } from "../../../settings-elements/order/pick-up-config-util";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import {
  getBodyStyles,
  getDeliveryDateStyles,
  getHeaderStyles,
  getLabelStyles,
  getLinkStyles,
  getTrackingNumberUrlStyles,
} from "../shared/block-styles";
import {
  CarrierBrandIcon,
  formatEventMessage,
  formatEventTimestamp,
  getShipmentStatusText,
} from "./tracking-details-shared";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const TrackingDetailsDesktop = memo(function TrackingDetailsDesktop({
  shipment,
  order,
  pickupConfig,
  stalledDays,
  optionalSubtext,
}: {
  shipment: TrackingPageShipment | undefined;
  order: TrackingPageOrder;
  pickupConfig: PickupConfig;
  stalledDays: number | null;
  optionalSubtext: string | undefined;
}) {
  const { returnAppSettings, pageSettings } =
    useRequiredContext(TrackingPageContext);

  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);
  const labelStyles = getLabelStyles(pageSettings);
  const deliveryDateStyles = getDeliveryDateStyles(pageSettings);
  const trackingUrlStyles = getTrackingNumberUrlStyles(pageSettings);
  const linkStyles = getLinkStyles(pageSettings);

  const isDelivered =
    shipment?.status === OrderTrackingShipmentStatus.DELIVERED;
  const deliveryDate = isDelivered
    ? shipment?.deliveredAt
    : shipment?.estimatedDeliveryAt;

  const eddDistrusted = useMemo(() => {
    const resolved = shipment?.carrier
      ? resolveCarrier(shipment.carrier)
      : undefined;
    return !!(resolved?.found && resolved.definition.distrustDeliveryEstimate);
  }, [shipment?.carrier]);

  const orderedEvents = useMemo(() => {
    const events = shipment?.trackingEvents?.length
      ? [...shipment.trackingEvents]
      : [];
    return events.sort(
      (a, b) =>
        (b.timestamp?.getTime?.() ?? 0) - (a.timestamp?.getTime?.() ?? 0),
    );
  }, [shipment?.trackingEvents]);

  const trackingCode = shipment?.trackingCode;
  const trackingUrl =
    getCarrierTrackingUrl(
      shipment?.carrier ?? undefined,
      trackingCode ?? undefined,
    ) ?? shipment?.trackingUrl;
  const showTrackingLink =
    !!trackingUrl && returnAppSettings.storeUrl !== "shopconzuri.myshopify.com";

  if (order.sourceName === "pos") {
    return null;
  }

  return (
    <Flex dir="column" flex={1} gap="lg" height="full" p="3xl" w="full">
      {/* Estimated delivery: title + weekday/month label + day-number hero */}
      <Flex dir="column" gap="xs">
        <Text fontSize="xl" fontWeight="bold" style={headerStyles}>
          {isDelivered ? "Delivered" : "Estimated delivery"}
        </Text>
        {deliveryDate ? (
          <>
            <Text fontSize="md" style={bodyStyles}>
              {DAYS_OF_WEEK[deliveryDate.getUTCDay()]},{" "}
              {MONTHS[deliveryDate.getUTCMonth()]}
            </Text>
            <Text
              fontSize="6xl"
              fontWeight="bold"
              style={{ ...headerStyles, ...deliveryDateStyles, lineHeight: 1 }}
            >
              {deliveryDate.getUTCDate()}
            </Text>
          </>
        ) : (
          <Text fontSize="md" style={bodyStyles}>
            {eddDistrusted ? "Unknown ETA" : "Awaiting ETA"}
          </Text>
        )}
      </Flex>

      {/* Status: label + value */}
      <Flex dir="column" gap="xxs">
        <Text
          fontSize="xs"
          fontWeight="medium"
          style={labelStyles}
          textColor="tertiary"
        >
          Status
        </Text>
        <Text fontSize="md" fontWeight="semibold" style={bodyStyles}>
          {getShipmentStatusText({
            cancelReason: order.cancelReason,
            pickupConfig,
            shipment,
            source: order.sourceName,
            stalledDays,
          })}
        </Text>
      </Flex>

      {/* Timeline — circles + connecting line, mirrors the mobile view. */}
      {orderedEvents.length > 0 && (
        <Flex dir="column" flex={1} gap="md" overflowY="auto">
          {orderedEvents.map((e, i) => {
            const isLast = i === orderedEvents.length - 1;
            const dotColor = bodyStyles.color ?? "currentColor";
            return (
              <Flex
                dir="column"
                gap="xxs"
                key={i}
                style={{ position: "relative", paddingLeft: 24 }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: 16,
                    height: 16,
                    borderRadius: 16,
                    border: `2px solid ${dotColor}`,
                    boxSizing: "border-box",
                  }}
                />
                {i !== 0 && (
                  <span
                    style={{
                      position: "absolute",
                      left: 3,
                      top: 3,
                      width: 10,
                      height: 10,
                      borderRadius: 12,
                      backgroundColor: dotColor,
                    }}
                  />
                )}
                {!isLast && (
                  <span
                    style={{
                      position: "absolute",
                      left: 7,
                      top: 16,
                      width: 2,
                      height: "calc(100% - 8px)",
                      backgroundColor: dotColor,
                      borderRadius: 16,
                    }}
                  />
                )}
                <Text fontSize="xs" fontWeight="semibold" style={bodyStyles}>
                  {formatEventTimestamp(e.timestamp)}
                </Text>
                <Text fontSize="xs" style={labelStyles}>
                  {formatEventMessage(e)}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      )}

      {/* Tracking — single row with inline carrier badge. */}
      {trackingCode && (
        <Flex dir="column" gap="xxs">
          <Text
            fontSize="xs"
            fontWeight="medium"
            style={labelStyles}
            textColor="tertiary"
          >
            Tracking
          </Text>
          <Flex align="center" gap="xs">
            <CarrierBrandIcon carrier={shipment?.carrier ?? null} />
            {showTrackingLink && trackingUrl ? (
              <ExternalLink
                showIcon={false}
                style={linkStyles}
                url={trackingUrl}
              >
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  style={{
                    ...trackingUrlStyles,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  textColor="secondary"
                >
                  {trackingCode}
                </Text>
              </ExternalLink>
            ) : (
              <Text
                fontSize="xs"
                fontWeight="semibold"
                style={{
                  ...bodyStyles,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                textColor="secondary"
              >
                {trackingCode}
              </Text>
            )}
          </Flex>
        </Flex>
      )}

      {optionalSubtext && (
        <Text fontSize="sm" style={labelStyles}>
          {optionalSubtext}
        </Text>
      )}
    </Flex>
  );
});
